<template>
  <div v-if="aggregatedPositions.length > 0" class="flex items-center gap-1">
    <div v-for="pos in aggregatedPositions" :key="pos.putOrCall">
      <div v-if="useTextLayout" :class="['text-end', getColorClasses(pos.putOrCall)]">
        <p class="font-medium">{{ formatSize(pos.total) }}</p>
        <p class="text-xs">{{ $t(`${pos.putOrCall}_position`) }}</p>
      </div>
      <div
        v-else
        v-tooltip.top="getTooltip(pos)"
        :class="[
          getFillClasses(pos.putOrCall),
          'rounded-sm px-1.5 py-px text-[10px] leading-tight text-center text-white uppercase whitespace-nowrap',
        ]"
        data-testid="position-item"
      >
        {{ formatSize(pos.total) }} {{ pos.putOrCall === 1 ? 'YES' : 'NO' }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { usePositions } from '@/stores/usePositions';
import { formatCompactNumber } from '@/utils/formatNumber';

interface Props {
  useTextLayout?: boolean;
  conids: number[];
  exchanges?: string[];
}

const props = defineProps<Props>();

const positionsStore = usePositions();

const aggregatedPositions = computed(() => {
  const byPutOrCall = new Map<number, { total: number; byExchange: Map<string, number> }>();
  for (let i = 0; i < props.conids.length; i++) {
    const conid = props.conids[i];
    if (!positionsStore.hasPosition(conid)) continue;
    const pos = positionsStore.getPositionByConid(conid)[0];
    const size = parseInt(pos.position, 10) || 0;
    if (size === 0) continue;
    const side = i % 2 === 0 ? 1 : 0;
    const existing = byPutOrCall.get(side) ?? { total: 0, byExchange: new Map<string, number>() };
    const absSize = Math.abs(size);
    existing.total += absSize;
    const exchangeIdx = Math.floor(i / 2);
    const exchange = props.exchanges?.[exchangeIdx];
    if (exchange) {
      existing.byExchange.set(exchange, (existing.byExchange.get(exchange) ?? 0) + absSize);
    }
    byPutOrCall.set(side, existing);
  }
  return [...byPutOrCall.entries()]
    .sort(([a], [b]) => b - a)
    .map(([putOrCall, { total, byExchange }]) => ({ putOrCall, total, byExchange }));
});

const getTooltip = (pos: { putOrCall: number; total: number; byExchange: Map<string, number> }): string => {
  const side = pos.putOrCall === 1 ? 'YES' : 'NO';
  if (pos.byExchange.size > 1) {
    const breakdown = [...pos.byExchange.entries()].map(([ex, qty]) => `${ex}: ${qty}`).join(', ');
    return `${side} Position: ${pos.total} (${breakdown})`;
  }
  if (pos.byExchange.size === 1) {
    const [ex] = pos.byExchange.keys();
    return `${side} Position: ${pos.total} on ${ex}`;
  }
  return `${side} Position: ${pos.total} contracts`;
};

const formatSize = formatCompactNumber;

const getColorClasses = (putOrCall: number): string => {
  return putOrCall === 1
    ? 'dark:border-(--p-green100) border-(--p-green200) dark:text-(--p-green100) text-(--p-green200)'
    : 'dark:border-(--p-red100) border-(--p-red200) dark:text-(--p-red100) text-(--p-red200)';
};

const getFillClasses = (putOrCall: number): string => {
  return putOrCall === 1 ? 'bg-yes' : 'bg-no';
};
</script>
